const FORMAT_CONTROL_CHARS_RE = /[\u00AD\u180E\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFE00-\uFE0F\uFEFF]/g;
const EMAIL_WHITESPACE_RE = /\s+/g;
const NON_BREAKING_SPACES_RE = /\u00A0/g;
const NARROW_NON_BREAKING_SPACE_RE = /\u202F/g;
const LINE_BREAKS_RE = /[\r\n]/g;
const EMAIL_TOKEN_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i;

function normalizeSmartPunctuation(value) {
  return value
    .replace(/[\u2018\u2019\u2032]/g, "'")
    .replace(/[\u201C\u201D\u2033]/g, '"')
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, '-')
    .replace(/\u00A0/g, ' ')
    .replace(/\u202F/g, ' ');
}

/**
 * Normalize auth email for resilient cross-device sign-in:
 * - NFKC normalizes full-width keyboard variants.
 * - strip Unicode format-control chars that can appear via paste/autofill.
 * - remove all whitespace (emails should not include spaces).
 * - lowercase for consistent matching.
 */
export function normalizeAuthEmail(value) {
  const cleaned = `${value ?? ''}`
    .normalize('NFKC')
    .replace(FORMAT_CONTROL_CHARS_RE, '')
    .replace(EMAIL_WHITESPACE_RE, '')
    .toLowerCase();

  if (!cleaned) return '';

  // Handle common mobile autofill formats:
  // - "Name <email@example.com>"
  // - "mailto:email@example.com"
  // - trailing separators like "email@example.com,"
  let candidate = cleaned.replace(/^mailto:/, '');
  const bracketMatch = candidate.match(/<([^>]+)>/);
  if (bracketMatch?.[1]) {
    candidate = bracketMatch[1];
  }
  candidate = candidate
    .split(/[;,]/)[0]
    .replace(/^['"(]+/, '')
    .replace(/[)'"]+$/, '');

  if (EMAIL_TOKEN_RE.test(candidate) && candidate === candidate.match(EMAIL_TOKEN_RE)?.[0]) {
    return candidate;
  }

  const token = cleaned.match(EMAIL_TOKEN_RE);
  return token ? token[0].toLowerCase() : '';
}

function pushUnique(values, candidate) {
  if (!values.includes(candidate)) {
    values.push(candidate);
  }
}

/**
 * Build safe retry candidates for password sign-in.
 * First attempt always preserves exact user input.
 * Later candidates only remove common accidental mobile artifacts.
 */
export function buildSignInPasswordCandidates(password) {
  const raw = `${password ?? ''}`;
  const nfkc = raw.normalize('NFKC');
  const trimmed = raw.trim();
  const sanitized = raw
    .replace(FORMAT_CONTROL_CHARS_RE, '')
    .replace(NON_BREAKING_SPACES_RE, ' ')
    .replace(NARROW_NON_BREAKING_SPACE_RE, ' ')
    .replace(LINE_BREAKS_RE, '');
  const sanitizedTrimmed = sanitized.trim();
  const smartPunctuationNormalized = normalizeSmartPunctuation(raw);
  const smartPunctuationNormalizedTrimmed = smartPunctuationNormalized.trim();
  const nfkcSmartSanitized = normalizeSmartPunctuation(nfkc)
    .replace(FORMAT_CONTROL_CHARS_RE, '')
    .replace(LINE_BREAKS_RE, '');
  const nfkcSmartSanitizedTrimmed = nfkcSmartSanitized.trim();

  const candidates = [];
  pushUnique(candidates, raw);
  pushUnique(candidates, nfkc);
  pushUnique(candidates, trimmed);
  pushUnique(candidates, sanitized);
  pushUnique(candidates, sanitizedTrimmed);
  pushUnique(candidates, smartPunctuationNormalized);
  pushUnique(candidates, smartPunctuationNormalizedTrimmed);
  pushUnique(candidates, nfkcSmartSanitized);
  pushUnique(candidates, nfkcSmartSanitizedTrimmed);

  return candidates;
}

export function isInvalidCredentialsError(error) {
  const message = `${error?.message ?? ''}`;
  return /invalid login credentials/i.test(message);
}
